import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { PenIcon } from "assets/icons";
import { Button, AlertMessage } from "components/atoms";
import {
  TextInput,
  NumberInput,
  DatePickerInput,
  GeneralSelectInput,
  TabbedFormToggle,
  TabConfig,
  EditTabConfig,
  RadioButtonGroup,
  InformationItem,
} from "components/molecules";
import { useUserData, useContentSanitizer } from "hooks";
import { ProfileHandling, formatDate, formatDateToString } from "services";
import { testOptions, englishTestRanges } from "types";

type TestType = "TOEFL" | "IELTS" | "PTE" | "CAE" | "CPE";

const disabledOptions = ["TOEFL", "CAE", "CPE"];

const getTestOptions = () => {
  return testOptions.map((option: any) => ({
    ...option,
    isDisabled: disabledOptions.includes(option.value),
  }));
};

const isValidScore = function (
  this: any,
  score: number | undefined | null,
  testType: TestType | ""
): boolean {
  return (
    !testType ||
    score === undefined ||
    score === null ||
    (score >= englishTestRanges[testType as TestType].min &&
      score <= englishTestRanges[testType as TestType].max)
  );
};

const englishProficiencySchema = Yup.object({
  isEnglishFirstLanguage: Yup.boolean(),
  firstLanguage: Yup.string().when("isEnglishFirstLanguage", {
    is: false,
    then: (schema) => schema.required("Please enter your first language"),
    otherwise: (schema) => schema.nullable(),
  }),
  hasEnglishInstructionStudies: Yup.boolean(),
  englishInstructionStudiesCompleted: Yup.string().when(
    "hasEnglishInstructionStudies",
    {
      is: true,
      then: (schema) =>
        schema.required("Please describe your English instruction studies"),
      otherwise: (schema) => schema.nullable(),
    }
  ),
  englishInstructionStudiesDuration: Yup.string().when(
    "hasEnglishInstructionStudies",
    {
      is: true,
      then: (schema) =>
        schema.required("Please enter the duration of your studies"),
      otherwise: (schema) => schema.nullable(),
    }
  ),

  englishTest: Yup.string(),
  reading: Yup.number().when("englishTest", {
    is: (val: string) => val && val !== "",
    then: (schema) =>
      schema
        .test(
          "score-range",
          "Invalid score for the selected test type",
          function (score) {
            return isValidScore.call(
              this,
              score,
              this.parent.englishTest as TestType
            );
          }
        )
        .required("Please enter a reading score"),
    otherwise: (schema) => schema.nullable(),
  }),
  writing: Yup.number().when("englishTest", {
    is: (val: string) => val && val !== "",
    then: (schema) =>
      schema
        .test(
          "score-range",
          "Invalid score for the selected test type",
          function (score) {
            return isValidScore.call(
              this,
              score,
              this.parent.englishTest as TestType
            );
          }
        )
        .required("Please enter a writing score"),
    otherwise: (schema) => schema.nullable(),
  }),
  speaking: Yup.number().when("englishTest", {
    is: (val: string) => val && val !== "",
    then: (schema) =>
      schema
        .test(
          "score-range",
          "Invalid score for the selected test type",
          function (score) {
            return isValidScore.call(
              this,
              score,
              this.parent.englishTest as TestType
            );
          }
        )
        .required("Please enter a speaking score"),
    otherwise: (schema) => schema.nullable(),
  }),
  listening: Yup.number().when("englishTest", {
    is: (val: string) => val && val !== "",
    then: (schema) =>
      schema
        .test(
          "score-range",
          "Invalid score for the selected test type",
          function (score) {
            return isValidScore.call(
              this,
              score,
              this.parent.englishTest as TestType
            );
          }
        )
        .required("Please enter a listening score"),
    otherwise: (schema) => schema.nullable(),
  }),
  useOfEnglish: Yup.number()
    .nullable()
    .when("englishTest", {
      is: (val: string) => val === "CAE" || val === "CPE",
      then: (schema) =>
        schema
          .test(
            "score-range",
            "Invalid score for the selected test type",
            function (score) {
              return isValidScore.call(
                this,
                score,
                this.parent.englishTest as TestType
              );
            }
          )
          .required("Please enter a use of English score"),
      otherwise: (schema) => schema.nullable(),
    }),
  overallScore: Yup.number().when("englishTest", {
    is: (val: string) => val && val !== "",
    then: (schema) =>
      schema
        .test(
          "score-range",
          "Invalid score for the selected test type",
          function (score) {
            return isValidScore.call(
              this,
              score,
              this.parent.englishTest as TestType
            );
          }
        )
        .required("Please enter an overall score"),
    otherwise: (schema) => schema.nullable(),
  }),
  dateOfCompletion: Yup.date().when("englishTest", {
    is: (val: string) => val && val !== "",
    then: (schema) => schema.required("Please choose a test date"),
    otherwise: (schema) => schema.nullable(),
  }),
  testReportFormNumber: Yup.string(),

  hasELICOS: Yup.boolean(),
  elicosInstitution: Yup.string().when("hasELICOS", {
    is: true,
    then: (schema) => schema.required("Please enter the ELICOS institution"),
    otherwise: (schema) => schema.nullable(),
  }),
  elicosStartDate: Yup.date()
    .nullable()
    .when("hasELICOS", {
      is: true,
      then: (schema) => schema.required("Please enter the ELICOS start date"),
      otherwise: (schema) => schema.nullable(),
    }),
  elicosEndDate: Yup.date()
    .nullable()
    .when("hasELICOS", {
      is: true,
      then: (schema) => schema.required("Please enter the ELICOS end date"),
      otherwise: (schema) => schema.nullable(),
    }),
  elicosWeeks: Yup.number()
    .nullable()
    .when("hasELICOS", {
      is: true,
      then: (schema) =>
        schema
          .min(1, "Must be at least 1 week")
          .required("Please enter the duration in weeks"),
      otherwise: (schema) => schema.nullable(),
    }),
});

const EnglishProficiencyPage: React.FC = () => {
  const { userData, fetchUserData } = useUserData();
  const { sanitizeContent } = useContentSanitizer();
  const [editingTab, setEditingTab] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const formik = useFormik({
    initialValues: {
      isEnglishFirstLanguage: null as boolean | null,
      firstLanguage: "",
      hasEnglishInstructionStudies: null as boolean | null,
      englishInstructionStudiesCompleted: "",
      englishInstructionStudiesDuration: "",

      englishTest: "",
      overallScore: null as number | null,
      listening: null as number | null,
      writing: null as number | null,
      reading: null as number | null,
      speaking: null as number | null,
      useOfEnglish: null as number | null,
      dateOfCompletion: null as Date | null,
      testReportFormNumber: "",

      hasELICOS: null as boolean | null,
      elicosInstitution: "",
      elicosStartDate: null as Date | null,
      elicosEndDate: null as Date | null,
      elicosWeeks: null as number | null,
    },
    validationSchema: englishProficiencySchema,
    onSubmit: () => {},
  });

  const resetFormToUserData = () => {
    if (!userData) return;

    const testScores = userData?.englishLanguageTestScores;

    formik.setValues({
      isEnglishFirstLanguage: testScores?.isEnglishFirstLanguage ?? null,
      firstLanguage: testScores?.firstLanguage || "",
      hasEnglishInstructionStudies:
        testScores?.hasEnglishInstructionStudies ?? null,
      englishInstructionStudiesCompleted:
        testScores?.englishInstructionStudiesCompleted || "",
      englishInstructionStudiesDuration:
        testScores?.englishInstructionStudiesDuration || "",

      englishTest: testScores?.englishTest || "",
      overallScore: testScores?.overallScore || null,
      listening: testScores?.listening || null,
      writing: testScores?.writing || null,
      reading: testScores?.reading || null,
      speaking: testScores?.speaking || null,
      useOfEnglish: testScores?.useOfEnglish || null,
      dateOfCompletion: testScores?.dateOfCompletion
        ? new Date(testScores.dateOfCompletion)
        : null,
      testReportFormNumber: testScores?.testReportFormNumber || "",

      hasELICOS: testScores?.hasELICOS ?? null,
      elicosInstitution: testScores?.elicosInstitution || "",
      elicosStartDate: testScores?.elicosStartDate
        ? new Date(testScores.elicosStartDate)
        : null,
      elicosEndDate: testScores?.elicosEndDate
        ? new Date(testScores.elicosEndDate)
        : null,
      elicosWeeks: testScores?.elicosWeeks || null,
    });
  };

  const getFormValue = (key: string) =>
    formik.values[key as keyof typeof formik.values];

  const isLanguageBackgroundComplete = () => {
    if (getFormValue("isEnglishFirstLanguage")) {
      return true;
    }

    const hasFirstLanguage = !!getFormValue("firstLanguage");

    if (getFormValue("hasEnglishInstructionStudies")) {
      const hasStudiesCompleted = !!getFormValue(
        "englishInstructionStudiesCompleted"
      );
      const hasDuration = !!getFormValue("englishInstructionStudiesDuration");
      return hasFirstLanguage && hasStudiesCompleted && hasDuration;
    }

    return hasFirstLanguage;
  };

  const isTestResultsComplete = () => {
    const testType = getFormValue("englishTest");

    if (!testType) {
      return true;
    }

    const hasBasicScores = !!(
      getFormValue("reading") !== null &&
      getFormValue("writing") !== null &&
      getFormValue("speaking") !== null &&
      getFormValue("listening") !== null &&
      getFormValue("overallScore") !== null &&
      getFormValue("dateOfCompletion")
    );

    if (testType === "CAE" || testType === "CPE") {
      return hasBasicScores && getFormValue("useOfEnglish") !== null;
    }

    return hasBasicScores;
  };

  const isELICOSComplete = () => {
    if (!getFormValue("hasELICOS")) {
      return true;
    }

    return !!(
      getFormValue("elicosInstitution") &&
      getFormValue("elicosStartDate") &&
      getFormValue("elicosEndDate") &&
      getFormValue("elicosWeeks")
    );
  };

  const validateFields = async (fields: string[]): Promise<boolean> => {
    const allErrors = await formik.validateForm();
    const relevantErrors = Object.keys(allErrors).filter((key) =>
      fields.includes(key)
    );
    if (relevantErrors.length > 0) {
      const touchedFields = fields.reduce(
        (acc: { [key: string]: boolean }, field: string) => ({
          ...acc,
          [field]: true,
        }),
        {}
      );
      formik.setTouched(touchedFields);
      setErrorMessage("Please fix the validation errors before saving.");
      return false;
    }
    return true;
  };

  const saveLanguageBackground = async (): Promise<boolean> => {
    try {
      const fields = [
        "firstLanguage",
        "englishInstructionStudiesCompleted",
        "englishInstructionStudiesDuration",
      ];
      if (!(await validateFields(fields))) return false;

      const input = {
        id: userData!.id,
        englishLanguageTestScores: {
          ...userData?.englishLanguageTestScores,
          isEnglishFirstLanguage: getFormValue("isEnglishFirstLanguage"),
          firstLanguage: sanitizeContent(
            getFormValue("firstLanguage") as string
          ),
          hasEnglishInstructionStudies: getFormValue(
            "hasEnglishInstructionStudies"
          ),
          englishInstructionStudiesCompleted: getFormValue(
            "englishInstructionStudiesCompleted"
          ),
          englishInstructionStudiesDuration: getFormValue(
            "englishInstructionStudiesDuration"
          ),
        },
      };

      await ProfileHandling.updateUser(input);
      await fetchUserData();
      setSuccessMessage("Language background updated successfully.");
      setErrorMessage("");
      return true;
    } catch (error) {
      setErrorMessage("Failed to update language background.");
      setSuccessMessage("");
      return false;
    }
  };

  const saveTestResults = async (): Promise<boolean> => {
    try {
      const fields = [
        "englishTest",
        "reading",
        "writing",
        "speaking",
        "listening",
        "useOfEnglish",
        "overallScore",
        "dateOfCompletion",
      ];
      if (!(await validateFields(fields))) return false;

      const input = {
        id: userData!.id,
        englishLanguageTestScores: {
          ...userData?.englishLanguageTestScores,
          englishTest: sanitizeContent(getFormValue("englishTest") as string),
          overallScore: getFormValue("overallScore"),
          reading: getFormValue("reading"),
          writing: getFormValue("writing"),
          speaking: getFormValue("speaking"),
          listening: getFormValue("listening"),
          useOfEnglish: getFormValue("useOfEnglish"),
          dateOfCompletion: getFormValue("dateOfCompletion")
            ? formatDate(getFormValue("dateOfCompletion") as Date)
            : null,
          testReportFormNumber: sanitizeContent(
            getFormValue("testReportFormNumber") as string
          ),
        },
      };

      await ProfileHandling.updateUser(input);
      await fetchUserData();
      setSuccessMessage("Test results updated successfully.");
      setErrorMessage("");
      return true;
    } catch (error) {
      setErrorMessage("Failed to update test results.");
      setSuccessMessage("");
      return false;
    }
  };

  const saveELICOS = async (): Promise<boolean> => {
    try {
      const fields = [
        "elicosInstitution",
        "elicosStartDate",
        "elicosEndDate",
        "elicosWeeks",
      ];
      if (!(await validateFields(fields))) return false;

      const input = {
        id: userData!.id,
        englishLanguageTestScores: {
          ...userData?.englishLanguageTestScores,
          hasELICOS: getFormValue("hasELICOS"),
          elicosInstitution: sanitizeContent(
            getFormValue("elicosInstitution") as string
          ),
          elicosStartDate: getFormValue("elicosStartDate")
            ? formatDateToString(getFormValue("elicosStartDate") as Date)
            : null,
          elicosEndDate: getFormValue("elicosEndDate")
            ? formatDate(getFormValue("elicosEndDate") as Date)
            : null,
          elicosWeeks: getFormValue("elicosWeeks"),
        },
      };

      await ProfileHandling.updateUser(input);
      await fetchUserData();
      setSuccessMessage("ELICOS details updated successfully.");
      setErrorMessage("");
      return true;
    } catch (error) {
      setErrorMessage("Failed to update ELICOS details.");
      setSuccessMessage("");
      return false;
    }
  };

  useEffect(() => {
    if (!userData) return;

    const testScores = userData?.englishLanguageTestScores;

    formik.setValues({
      // Language Background
      isEnglishFirstLanguage: testScores?.isEnglishFirstLanguage ?? null,
      firstLanguage: testScores?.firstLanguage || "",
      hasEnglishInstructionStudies: testScores?.hasEnglishInstructionStudies,
      englishInstructionStudiesCompleted:
        testScores?.englishInstructionStudiesCompleted || "",
      englishInstructionStudiesDuration:
        testScores?.englishInstructionStudiesDuration || "",

      // English Test Results
      englishTest: testScores?.englishTest || "",
      overallScore: testScores?.overallScore || null,
      listening: testScores?.listening || null,
      writing: testScores?.writing || null,
      reading: testScores?.reading || null,
      speaking: testScores?.speaking || null,
      useOfEnglish: testScores?.useOfEnglish || null,
      dateOfCompletion: testScores?.dateOfCompletion
        ? new Date(testScores.dateOfCompletion)
        : null,
      testReportFormNumber: testScores?.testReportFormNumber || "",

      // ELICOS
      hasELICOS: testScores?.hasELICOS ?? null,
      elicosInstitution: testScores?.elicosInstitution || "",
      elicosStartDate: testScores?.elicosStartDate
        ? new Date(testScores.elicosStartDate)
        : null,
      elicosEndDate: testScores?.elicosEndDate
        ? new Date(testScores.elicosEndDate)
        : null,
      elicosWeeks: testScores?.elicosWeeks || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const displayTabs: TabConfig[] = [
    {
      id: "language-background",
      label: "Language Background",
      description: "First language and English instruction history",
      component: (
        <div className="form-section">
          <div className="section-header">
            <h4>Language Background</h4>
            <Button
              text={<PenIcon />}
              onClick={() => setEditingTab("language-background")}
              variant="borderless"
            />
          </div>
          <dl>
            <InformationItem
              label="English First Language:"
              value={
                getFormValue("isEnglishFirstLanguage") === null
                  ? "Not Provided"
                  : getFormValue("isEnglishFirstLanguage")
                    ? "Yes"
                    : "No"
              }
            />
            {!getFormValue("isEnglishFirstLanguage") && (
              <>
                <InformationItem
                  label="First Language:"
                  value={String(
                    getFormValue("firstLanguage") || "Not Provided"
                  )}
                />
                <InformationItem
                  label="Previous English Instruction:"
                  value={
                    getFormValue("hasEnglishInstructionStudies") ? "Yes" : "No"
                  }
                />
                {getFormValue("hasEnglishInstructionStudies") && (
                  <>
                    <InformationItem
                      label="Studies Completed:"
                      value={String(
                        getFormValue("englishInstructionStudiesCompleted") ||
                          "Not Provided"
                      )}
                    />
                    <InformationItem
                      label="Duration:"
                      value={String(
                        getFormValue("englishInstructionStudiesDuration") ||
                          "Not Provided"
                      )}
                    />
                  </>
                )}
              </>
            )}
          </dl>
        </div>
      ),
    },
    {
      id: "test-results",
      label: "Test Results",
      description: "English proficiency test scores",
      component: (
        <div className="form-section">
          <div className="section-header">
            <h4>English Test Results</h4>
            <Button
              text={<PenIcon />}
              onClick={() => setEditingTab("test-results")}
              variant="borderless"
            />
          </div>
          <dl>
            <InformationItem
              label="Test Type:"
              value={String(getFormValue("englishTest") || "Not Provided")}
            />
            <InformationItem
              label="Reading Score:"
              value={String(getFormValue("reading") || "Not Provided")}
            />
            <InformationItem
              label="Writing Score:"
              value={String(getFormValue("writing") || "Not Provided")}
            />
            <InformationItem
              label="Speaking Score:"
              value={String(getFormValue("speaking") || "Not Provided")}
            />
            <InformationItem
              label="Listening Score:"
              value={String(getFormValue("listening") || "Not Provided")}
            />
            {(getFormValue("englishTest") === "CAE" ||
              getFormValue("englishTest") === "CPE") && (
              <InformationItem
                label="Use of English Score:"
                value={String(getFormValue("useOfEnglish") || "Not Provided")}
              />
            )}
            <InformationItem
              label="Overall Score:"
              value={String(getFormValue("overallScore") || "Not Provided")}
            />
            <InformationItem
              label="Test Date:"
              value={
                getFormValue("dateOfCompletion")
                  ? formatDateToString(getFormValue("dateOfCompletion") as Date)
                  : "Not Provided"
              }
            />
            <InformationItem
              label="Test Report Number:"
              value={String(
                getFormValue("testReportFormNumber") || "Not Provided"
              )}
            />
          </dl>
        </div>
      ),
    },
    {
      id: "elicos",
      label: "ELICOS Details",
      description: "English Language Intensive Courses",
      component: (
        <div className="form-section">
          <div className="section-header">
            <h4>ELICOS Details</h4>
            <Button
              text={<PenIcon />}
              onClick={() => setEditingTab("elicos")}
              variant="borderless"
            />
          </div>
          <dl>
            <InformationItem
              label="Completed English Language Intensive Courses for Overseas Students (ELICOS):"
              value={
                getFormValue("hasELICOS") === null
                  ? "Not Provided"
                  : getFormValue("hasELICOS")
                    ? "Yes"
                    : "No"
              }
            />
            {getFormValue("hasELICOS") && (
              <>
                <InformationItem
                  label="Institution:"
                  value={String(
                    getFormValue("elicosInstitution") || "Not Provided"
                  )}
                />
                <InformationItem
                  label="Start Date:"
                  value={
                    getFormValue("elicosStartDate")
                      ? formatDateToString(
                          getFormValue("elicosStartDate") as Date
                        )
                      : "Not Provided"
                  }
                />
                <InformationItem
                  label="End Date:"
                  value={
                    getFormValue("elicosEndDate")
                      ? formatDateToString(
                          getFormValue("elicosEndDate") as Date
                        )
                      : "Not Provided"
                  }
                />
                <InformationItem
                  label="Duration (weeks):"
                  value={String(getFormValue("elicosWeeks") || "Not Provided")}
                />
              </>
            )}
          </dl>
        </div>
      ),
    },
  ];

  const editTabConfigs: EditTabConfig[] = [
    {
      id: "language-background",
      label: "Language Background",
      title: "Edit Language Background",
      onSave: saveLanguageBackground,
      onCancel: resetFormToUserData,
      saveDisabled: !isLanguageBackgroundComplete(),
      formFields: (
        <>
          <RadioButtonGroup
            name="isEnglishFirstLanguage"
            label="Is English your first language?"
            value={getFormValue("isEnglishFirstLanguage") as boolean}
            onChange={(value) =>
              formik.setFieldValue("isEnglishFirstLanguage", value)
            }
          />
          {!getFormValue("isEnglishFirstLanguage") && (
            <>
              <TextInput
                id="firstLanguage"
                label="First Language *"
                placeholder="Enter your first language"
                getFieldProps={() => ({
                  value: getFormValue("firstLanguage"),
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                })}
                error={formik.errors.firstLanguage}
                touched={formik.touched.firstLanguage}
              />
              <RadioButtonGroup
                name="hasEnglishInstructionStudies"
                label="Have you completed any studies where English was the language of instruction?"
                value={
                  getFormValue("hasEnglishInstructionStudies") as boolean | null
                }
                onChange={(value) =>
                  formik.setFieldValue("hasEnglishInstructionStudies", value)
                }
              />
              {getFormValue("hasEnglishInstructionStudies") && (
                <>
                  <TextInput
                    id="englishInstructionStudiesCompleted"
                    label="Studies Completed *"
                    placeholder="Describe the studies you completed"
                    getFieldProps={() => ({
                      value: getFormValue("englishInstructionStudiesCompleted"),
                      onChange: formik.handleChange,
                      onBlur: formik.handleBlur,
                    })}
                    error={formik.errors.englishInstructionStudiesCompleted}
                    touched={formik.touched.englishInstructionStudiesCompleted}
                  />
                  <TextInput
                    id="englishInstructionStudiesDuration"
                    label="Duration *"
                    placeholder="Enter the duration (e.g., 2 years, 6 months)"
                    getFieldProps={() => ({
                      value: getFormValue("englishInstructionStudiesDuration"),
                      onChange: formik.handleChange,
                      onBlur: formik.handleBlur,
                    })}
                    error={formik.errors.englishInstructionStudiesDuration}
                    touched={formik.touched.englishInstructionStudiesDuration}
                  />
                </>
              )}
            </>
          )}
        </>
      ),
    },
    {
      id: "test-results",
      label: "Test Results",
      title: "Edit Test Results",
      onSave: saveTestResults,
      onCancel: resetFormToUserData,
      saveDisabled: !isTestResultsComplete(),
      formFields: (
        <>
          <GeneralSelectInput
            id="englishTest"
            label="Test Type *"
            options={getTestOptions()}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={getFormValue("englishTest") as string}
            error={formik.errors.englishTest}
            touched={formik.touched.englishTest}
          />
          <NumberInput
            id="reading"
            label="Reading Score *"
            placeholder="Enter reading score"
            getFieldProps={() => ({
              value: getFormValue("reading"),
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            })}
            error={formik.errors.reading}
            touched={formik.touched.reading}
          />
          <NumberInput
            id="writing"
            label="Writing Score *"
            placeholder="Enter writing score"
            getFieldProps={() => ({
              value: getFormValue("writing"),
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            })}
            error={formik.errors.writing}
            touched={formik.touched.writing}
          />
          <NumberInput
            id="speaking"
            label="Speaking Score *"
            placeholder="Enter speaking score"
            getFieldProps={() => ({
              value: getFormValue("speaking"),
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            })}
            error={formik.errors.speaking}
            touched={formik.touched.speaking}
          />
          <NumberInput
            id="listening"
            label="Listening Score *"
            placeholder="Enter listening score"
            getFieldProps={() => ({
              value: getFormValue("listening"),
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            })}
            error={formik.errors.listening}
            touched={formik.touched.listening}
          />
          {(getFormValue("englishTest") === "CAE" ||
            getFormValue("englishTest") === "CPE") && (
            <NumberInput
              id="useOfEnglish"
              label="Use of English Score *"
              placeholder="Enter use of English score"
              getFieldProps={() => ({
                value: getFormValue("useOfEnglish"),
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
              })}
              error={formik.errors.useOfEnglish}
              touched={formik.touched.useOfEnglish}
            />
          )}
          <NumberInput
            id="overallScore"
            label="Overall Score *"
            placeholder="Enter overall score"
            getFieldProps={() => ({
              value: getFormValue("overallScore"),
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            })}
            error={formik.errors.overallScore}
            touched={formik.touched.overallScore}
          />
          <DatePickerInput
            id="dateOfCompletion"
            className="dateOfCompletion"
            label="Test Date *"
            value={getFormValue("dateOfCompletion") as Date | null}
            onChange={(date) => formik.setFieldValue("dateOfCompletion", date)}
            preset="testDate"
          />
          <TextInput
            id="testReportFormNumber"
            label="Test Report Number *"
            placeholder="Enter test report form number"
            getFieldProps={() => ({
              value: getFormValue("testReportFormNumber"),
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            })}
            error={formik.errors.testReportFormNumber}
            touched={formik.touched.testReportFormNumber}
          />
        </>
      ),
    },
    {
      id: "elicos",
      label: "ELICOS Details",
      title: "Edit ELICOS Details",
      onSave: saveELICOS,
      onCancel: resetFormToUserData,
      saveDisabled: !isELICOSComplete(),
      formFields: (
        <>
          <RadioButtonGroup
            name="hasELICOS"
            label="Have you completed English Language Intensive Courses for Overseas Students (ELICOS)?"
            value={getFormValue("hasELICOS") as boolean}
            onChange={(value) => formik.setFieldValue("hasELICOS", value)}
          />
          {getFormValue("hasELICOS") && (
            <>
              <TextInput
                id="elicosInstitution"
                label="ELICOS Institution *"
                placeholder="Enter institution name"
                getFieldProps={() => ({
                  value: getFormValue("elicosInstitution"),
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                })}
                error={formik.errors.elicosInstitution}
                touched={formik.touched.elicosInstitution}
              />
              <DatePickerInput
                id="elicosStartDate"
                className="elicosStartDate"
                label="Start Date *"
                value={getFormValue("elicosStartDate") as Date | null}
                onChange={(date) =>
                  formik.setFieldValue("elicosStartDate", date)
                }
                preset="pastDate"
              />
              <DatePickerInput
                id="elicosEndDate"
                className="elicosEndDate"
                label="End Date *"
                value={getFormValue("elicosEndDate") as Date | null}
                onChange={(date) => formik.setFieldValue("elicosEndDate", date)}
                preset="pastDate"
              />
              <NumberInput
                id="elicosWeeks"
                label="Duration (weeks) *"
                placeholder="Enter duration in weeks"
                getFieldProps={() => ({
                  value: getFormValue("elicosWeeks"),
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                })}
                error={formik.errors.elicosWeeks}
                touched={formik.touched.elicosWeeks}
              />
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <div className="form-group english-proficiency-page">
      {successMessage && (
        <AlertMessage type="success">{successMessage}</AlertMessage>
      )}
      {errorMessage && <AlertMessage type="error">{errorMessage}</AlertMessage>}
      <TabbedFormToggle
        headerComponent={
          <div className="form-group header">
            <h2>English Proficiency</h2>
          </div>
        }
        displayTabs={displayTabs}
        editTabConfigs={editTabConfigs}
        formikInstance={formik}
        editingTab={editingTab}
        onTabEditToggle={setEditingTab}
      />
    </div>
  );
};

export default EnglishProficiencyPage;
